import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Settings, Plus, Link2, Tags, Trophy, TrendingDown } from 'lucide-react';
import config from '../config';
import { useSelectedMetrics, getCosClassName, getCpaClassName, useComponentMetrics, setModuleVisibility } from '../utils/metricsUtils'
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant modulaire pour afficher les tops et flops groupes de produits (item_group_id)
 * Top groupes = 5 groupes les plus vendeurs
 * Flop groupes = 5 groupes les plus cliqués sans vente
 *
 * Ce composant charge ses propres données depuis l'endpoint modulaire
 */
const TopFlopItemGroupsTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  token,
  setError,
  preloadedData = null,
  // Props pour filtrage par labels
  selectedLabelsFilter = [],
  campaignMatchesLabelsFilter = () => true,
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'topflop_itemgroups',
  loadingComparison = false
}) => {
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');
  const [showMetricsConfigTop, setShowMetricsConfigTop] = useState(false);
  const [showMetricsConfigFlop, setShowMetricsConfigFlop] = useState(false);

  // Configuration des métriques séparées pour Top et Flop
  const topMetrics = useComponentMetrics('topflop_itemgroups_top', selectedAccount?.id || 'global');
  const flopMetrics = useComponentMetrics('topflop_itemgroups_flop', selectedAccount?.id || 'global');

  // Fonctions de visibilité des métriques pour Top et Flop
  const isTopMetricVisible = useCallback((metricKey) => {
    return topMetrics.metrics.includes(metricKey);
  }, [topMetrics.metrics]);

  const isFlopMetricVisible = useCallback((metricKey) => {
    return flopMetrics.metrics.includes(metricKey);
  }, [flopMetrics.metrics]);

  // États locaux pour les données
  const [topItemGroups, setTopItemGroups] = useState([]);
  const [flopItemGroups, setFlopItemGroups] = useState([]);
  const [previousTopItemGroups, setPreviousTopItemGroups] = useState([]);
  const [previousFlopItemGroups, setPreviousFlopItemGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0); // Incrémenter pour forcer le rechargement
  const [merchantStatus, setMerchantStatus] = useState(null);

  // Construire une URL de retour complète avec le type de dashboard et le compte
  const buildReturnUrl = useCallback(() => {
    const baseUrl = window.location.origin;
    const accountId = selectedAccount?.id || '';
    // Construire l'URL avec le hash complet
    return `${baseUrl}/#dashboard/google/global${accountId ? `?account=${accountId}` : ''}`;
  }, [selectedAccount]);
  const [addingMerchant, setAddingMerchant] = useState(false);

  // Fonction pour ajouter rapidement un compte Merchant détecté
  const handleAddMerchant = async (merchantId) => {
    if (!merchantId || addingMerchant) return;

    setAddingMerchant(true);
    try {
      console.log(`🏷️ [TOP-FLOP-ITEMGROUPS] Ajout du compte Merchant ${merchantId}...`);

      const response = await axios.post(
        `${config.API_URL}/api/dashboard/components/top-flop-itemgroups/add-merchant`,
        {
          merchantId: merchantId,
          googleAdsAccountId: selectedAccount?.id
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        console.log(`✅ [TOP-FLOP-ITEMGROUPS] Compte Merchant ajouté avec succès, rechargement...`);
        // Forcer le rechargement des données
        setMerchantStatus(null);
        setReloadTrigger(prev => prev + 1);
      } else {
        console.error(`❌ [TOP-FLOP-ITEMGROUPS] Erreur ajout Merchant:`, response.data.error);
        if (setError) setError(response.data.error);
      }
    } catch (error) {
      // Si le compte existe déjà (code 409), on considère que c'est un succès et on recharge
      if (error.response?.status === 409) {
        console.log(`✅ [TOP-FLOP-ITEMGROUPS] Compte Merchant ${merchantId} existe déjà, rechargement des données...`);
        setMerchantStatus(null);
        setReloadTrigger(prev => prev + 1);
      } else {
        console.error(`❌ [TOP-FLOP-ITEMGROUPS] Erreur ajout Merchant:`, error);
        if (setError) setError(error.response?.data?.error || 'Erreur lors de l\'ajout du compte Merchant');
      }
    } finally {
      setAddingMerchant(false);
    }
  };





  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    // Ne pas charger les données si le module est masqué (même comportement que TopFlopProductsTableModular)
    if (!isVisible) return;

    if (!selectedAccount || !selectedPeriod || !token) {
      return;
    }

    const loadTopFlopItemGroups = async () => {
      try {
        setLoading(true);

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null,
          limit: 5
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/top-flop-itemgroups`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          const topData = response.data.data.topItemGroups || [];
          const flopData = response.data.data.flopItemGroups || [];
          const merchStatus = response.data.data.merchantStatus || null;

          setTopItemGroups(topData);
          setFlopItemGroups(flopData);
          setMerchantStatus(merchStatus);
          setIsDataLoaded(true);

          // Retirer le module des modules masqués si le composant ne devrait pas exister
          const hasNoData = topData.length === 0 && flopData.length === 0;
          const hasLinkedMerchant = merchStatus?.linkedMerchantIds?.length > 0;
          const hasMerchantIssue = merchStatus && ['NO_MERCHANT_CONNECTION', 'NO_MERCHANT_ACCOUNT', 'MERCHANT_NOT_LINKED', 'MERCHANT_ACCESS_DENIED', 'CHOOSE_MERCHANT_ACCOUNT'].includes(merchStatus.status);
          const shouldNotExist = hasNoData && (!hasLinkedMerchant || !hasMerchantIssue);

          if (shouldNotExist) {
            // Forcer la visibilité à true pour retirer de la liste des modules masqués
            setModuleVisibility('topflop_itemgroups', true, selectedAccount?.id || 'global');
            console.log('📦 [TOP-FLOP-ITEMGROUPS] Retiré des modules masqués - composant non applicable');
          }

          // Charger les données de la période précédente si comparaison activée
          if (compareEnabled && comparisonType) {
            let currentStartDate = customStartDate;
            let currentEndDate = customEndDate;
            if (!currentStartDate || !currentEndDate) {
              const dateRange = getDateRangeFromPeriod(selectedPeriod);
              currentStartDate = dateRange.startDate;
              currentEndDate = dateRange.endDate;
            }
            const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
            console.log(`📦 [TOP-FLOP-ITEMGROUPS] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

            try {
              const prevRequestBody = {
                sources: [selectedAccount.id],
                period: 'CUSTOM',
                customStartDate: prevPeriod.startDate,
                customEndDate: prevPeriod.endDate,
                campaignFilter: selectedCampaignFilter || null,
                campaignTypeFilter: selectedCampaignTypeFilter || null,
                limit: 5
              };

              const prevResponse = await axios.post(
                `${config.API_URL}/api/dashboard/components/top-flop-itemgroups`,
                prevRequestBody,
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );

              if (prevResponse.data.success) {
                console.log(`📦 [TOP-FLOP-ITEMGROUPS] Données période précédente chargées:`, {
                  topCount: prevResponse.data.data.topItemGroups?.length || 0,
                  flopCount: prevResponse.data.data.flopItemGroups?.length || 0
                });
                setPreviousTopItemGroups(prevResponse.data.data.topItemGroups || []);
                setPreviousFlopItemGroups(prevResponse.data.data.flopItemGroups || []);
              }
            } catch (prevError) {
              console.warn(`⚠️ [TOP-FLOP-ITEMGROUPS] Erreur chargement période précédente:`, prevError.message);
              setPreviousTopItemGroups([]);
              setPreviousFlopItemGroups([]);
            }
          } else {
            setPreviousTopItemGroups([]);
            setPreviousFlopItemGroups([]);
          }
        } else {
          console.error('❌ [TOP-FLOP-ITEMGROUPS-MODULAR] API retourne success=false:', response.data);
          setError('Erreur lors du chargement des tops/flops groupes produits');
        }
      } catch (error) {
        console.error('❌ [TOP-FLOP-ITEMGROUPS-MODULAR] Erreur chargement:', error);
        console.error('❌ [TOP-FLOP-ITEMGROUPS-MODULAR] Détails:', error.response?.data || error.message);
        setError('Erreur lors du chargement des tops/flops groupes produits');
      } finally {
        setLoading(false);
      }
    };

    loadTopFlopItemGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, token, compareEnabled, comparisonType, reloadTrigger, isVisible]);

  // Charger les données de comparaison séparément quand compareEnabled change
  useEffect(() => {
    const loadComparisonDataOnly = async () => {
      // Ne pas charger si le module est masqué
      if (!isVisible) {
        return;
      }
      if (!compareEnabled || !comparisonType || !selectedAccount || !token || !isDataLoaded) {
        if (!compareEnabled) {
          setPreviousTopItemGroups([]);
          setPreviousFlopItemGroups([]);
        }
        return;
      }

      // Calculer les dates de la période actuelle
      let currentStartDate = customStartDate;
      let currentEndDate = customEndDate;
      if (!currentStartDate || !currentEndDate) {
        const dateRange = getDateRangeFromPeriod(selectedPeriod);
        currentStartDate = dateRange.startDate;
        currentEndDate = dateRange.endDate;
      }

      const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
      console.log(`📦 [TOP-FLOP-ITEMGROUPS] Chargement données comparaison (effet séparé): ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

      setLoadingPreviousPeriod(true);
      try {
        const prevRequestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate,
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null,
          limit: 5
        };

        const prevResponse = await axios.post(
          `${config.API_URL}/api/dashboard/components/top-flop-itemgroups`,
          prevRequestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (prevResponse.data.success) {
          console.log(`📦 [TOP-FLOP-ITEMGROUPS] Données comparaison chargées:`, {
            topCount: prevResponse.data.data.topItemGroups?.length || 0,
            flopCount: prevResponse.data.data.flopItemGroups?.length || 0
          });
          setPreviousTopItemGroups(prevResponse.data.data.topItemGroups || []);
          setPreviousFlopItemGroups(prevResponse.data.data.flopItemGroups || []);
        }
      } catch (error) {
        console.warn(`⚠️ [TOP-FLOP-ITEMGROUPS] Erreur chargement données comparaison:`, error.message);
        setPreviousTopItemGroups([]);
        setPreviousFlopItemGroups([]);
      } finally {
        setLoadingPreviousPeriod(false);
      }
    };

    loadComparisonDataOnly();
  }, [compareEnabled, comparisonType, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, token, isDataLoaded, isVisible]);

  // Calcul des totaux pour les top groupes
  const topTotals = useMemo(() => {
    if (!topItemGroups.length) return null;

    const baseTotals = topItemGroups.reduce((acc, group) => ({
      impressions: acc.impressions + (group.impressions || 0),
      clicks: acc.clicks + (group.clicks || 0),
      conversions: acc.conversions + (group.conversions || 0),
      cost: acc.cost + (group.cost || 0),
      conversionsValue: acc.conversionsValue + (group.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, conversions: 0, cost: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [topItemGroups]);

  // Calcul des totaux pour les flop groupes
  const flopTotals = useMemo(() => {
    if (!flopItemGroups.length) return null;

    const baseTotals = flopItemGroups.reduce((acc, group) => ({
      impressions: acc.impressions + (group.impressions || 0),
      clicks: acc.clicks + (group.clicks || 0),
      cost: acc.cost + (group.cost || 0),
      conversions: acc.conversions + (group.conversions || 0),
      conversionsValue: acc.conversionsValue + (group.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [flopItemGroups]);

  // Calcul des totaux de la période précédente pour les top groupes
  const previousTopTotals = useMemo(() => {
    if (!compareEnabled || !previousTopItemGroups.length) return null;

    const baseTotals = previousTopItemGroups.reduce((acc, group) => ({
      impressions: acc.impressions + (group.impressions || 0),
      clicks: acc.clicks + (group.clicks || 0),
      conversions: acc.conversions + (group.conversions || 0),
      cost: acc.cost + (group.cost || 0),
      conversionsValue: acc.conversionsValue + (group.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, conversions: 0, cost: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousTopItemGroups]);

  // Calcul des totaux de la période précédente pour les flop groupes
  const previousFlopTotals = useMemo(() => {
    if (!compareEnabled || !previousFlopItemGroups.length) return null;

    const baseTotals = previousFlopItemGroups.reduce((acc, group) => ({
      impressions: acc.impressions + (group.impressions || 0),
      clicks: acc.clicks + (group.clicks || 0),
      cost: acc.cost + (group.cost || 0),
      conversions: acc.conversions + (group.conversions || 0),
      conversionsValue: acc.conversionsValue + (group.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousFlopItemGroups]);

  // Debug des conditions d'affichage
  console.log('📦 [TOP-FLOP-ITEMGROUPS] État:', {
    loading,
    isDataLoaded,
    topItemGroupsCount: topItemGroups.length,
    flopItemGroupsCount: flopItemGroups.length,
    previousTopItemGroupsCount: previousTopItemGroups.length,
    previousFlopItemGroupsCount: previousFlopItemGroups.length,
    selectedAccount: selectedAccount?.id,
    selectedPeriod,
    hasToken: !!token,
    compareEnabled
  });

  // Attendre que les paramètres nécessaires soient disponibles
  if (!selectedAccount || !token) {
    return null;
  }

  // Ne pas afficher si pas de données et pas en chargement (mais seulement si on a essayé de charger)
  if (!loading && !isDataLoaded && !preloadedData) {
    console.log('📦 [TOP-FLOP-ITEMGROUPS] Masqué: pas de données et pas en chargement');
    return null;
  }

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  // Vérifier si aucun groupe trouvé (après chargement)
  const hasNoGroups = !loading && isDataLoaded && topItemGroups.length === 0 && flopItemGroups.length === 0;

  // Masquer complètement le composant s'il n'y a pas de données et qu'on a fini de charger
  // (même comportement que TopFlopProductsTableModular)
  if (hasNoGroups) {
    // Pour les clients : toujours masquer s'il n'y a pas de données
    if (userRole === 'CLIENT') {
      console.log('📦 [TOP-FLOP-ITEMGROUPS] Masqué pour CLIENT - aucune donnée');
      return null;
    }

    // Vérifier si un Merchant Center a été détecté via les campagnes Shopping
    const hasLinkedMerchant = merchantStatus?.linkedMerchantIds?.length > 0;

    if (!hasLinkedMerchant) {
      // Pas de Merchant Center détecté = pas de campagnes Shopping = masquer le composant
      console.log('📦 [TOP-FLOP-ITEMGROUPS] Masqué - aucun Merchant Center détecté (pas de campagnes Shopping)');
      return null;
    }

    // Un Merchant a été détecté mais problème d'accès → afficher les boutons pour corriger
    const hasMerchantIssue = merchantStatus && ['NO_MERCHANT_CONNECTION', 'NO_MERCHANT_ACCOUNT', 'MERCHANT_NOT_LINKED', 'MERCHANT_ACCESS_DENIED', 'CHOOSE_MERCHANT_ACCOUNT'].includes(merchantStatus.status);
    if (!hasMerchantIssue) {
      // Pas de problème Merchant spécifique, juste pas de données
      console.log('📦 [TOP-FLOP-ITEMGROUPS] Masqué - aucun produit avec item_group_id');
      return null;
    }
    // Sinon, afficher le composant avec les boutons d'action pour corriger le problème Merchant
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Tags size={16} />&nbsp;Top & Flop Groupes Produits</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Top/Flop Groupes de produits"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <div className="table-container" style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}>
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des tops & flops groupes produits...<br /><br />
            </div>
          ) : compareEnabled && (loadingComparison || loadingPreviousPeriod) ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          ) : hasNoGroups ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              {/* Afficher les boutons d'action selon le statut Merchant */}
              {merchantStatus?.status === 'NO_MERCHANT_CONNECTION' ? (
                <>
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Connexion Google Merchant Center requise</strong>
                  </p>
                  <p style={{ fontSize: '0.9em', marginBottom: '16px', color: '#888' }}>
                    Pour afficher les groupes de produits, connectez votre compte Google Merchant Center.
                  </p>
                  {userRole !== 'CLIENT' && (
                    <button
                      onClick={() => {
                        // Passer l'URL complète du dashboard avec le compte pour y retourner après OAuth
                        const returnUrl = encodeURIComponent(buildReturnUrl());
                        window.location.href = `${config.API_URL}/api/auth/google-merchant-api?token=${token}&returnUrl=${returnUrl}`;
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        backgroundColor: '#4285F4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.95em',
                        fontWeight: '500'
                      }}
                    >
                      <Link2 size={16} />
                      Connecter Google Merchant Center
                    </button>
                  )}
                </>
              ) : merchantStatus?.status === 'CHOOSE_MERCHANT_ACCOUNT' ? (
                // Afficher la liste des comptes Merchant accessibles pour sélection
                <>
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Sélectionnez un compte Merchant Center</strong>
                  </p>
                  <p style={{ fontSize: '0.9em', marginBottom: '16px', color: '#888' }}>
                    {merchantStatus?.accessibleMerchantIds?.length} compte(s) Merchant Center lié(s) à vos campagnes Shopping sont accessibles.
                  </p>
                  {userRole !== 'CLIENT' && merchantStatus?.accessibleMerchantIds?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                      {merchantStatus.accessibleMerchantIds.map(merchant => (
                        <button
                          key={merchant.merchantId}
                          onClick={() => handleAddMerchant(merchant.merchantId)}
                          disabled={addingMerchant}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            padding: '12px 20px',
                            backgroundColor: addingMerchant ? '#e5e7eb' : '#f8fafc',
                            color: '#374151',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            cursor: addingMerchant ? 'wait' : 'pointer',
                            fontSize: '0.95em',
                            width: '100%',
                            maxWidth: '350px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!addingMerchant) {
                              e.currentTarget.style.backgroundColor = '#4285F4';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.borderColor = '#4285F4';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8fafc';
                            e.currentTarget.style.color = '#374151';
                            e.currentTarget.style.borderColor = '#e5e7eb';
                          }}
                        >
                          <span style={{ fontWeight: '500' }}>{merchant.merchantName}</span>
                          <span style={{ fontSize: '0.85em', opacity: 0.7 }}>ID: {merchant.merchantId}</span>
                        </button>
                      ))}
                      <p style={{ fontSize: '0.8em', color: '#999', margin: '8px 0 0 0' }}>
                        Cliquez sur un compte pour l'ajouter à votre bibliothèque
                      </p>
                    </div>
                  )}
                </>
              ) : merchantStatus?.status === 'NO_MERCHANT_ACCOUNT' || merchantStatus?.status === 'MERCHANT_NOT_LINKED' || merchantStatus?.status === 'MERCHANT_ACCESS_DENIED' ? (
                (() => {
                  // Vérifier si le Merchant détecté existe déjà dans la bibliothèque
                  const detectedMerchantId = merchantStatus?.linkedMerchantIds?.[0]?.merchantId;
                  const merchantExistsInLibrary = detectedMerchantId && merchantStatus?.existingMerchantAccounts?.some(
                    acc => acc.sourceId === detectedMerchantId
                  );

                  // Vérifier si une connexion Merchant API existe
                  const hasMerchantConnection = merchantStatus?.hasMerchantConnection;

                  // Si connexion existe mais compte pas dans bibliothèque → proposer d'ajouter
                  const canAddAccount = hasMerchantConnection && !merchantExistsInLibrary && detectedMerchantId;

                  return (
                    <>
                      <p style={{ marginBottom: '12px' }}>
                        <strong>
                          {merchantExistsInLibrary
                            ? 'Reconnexion requise au Merchant Center'
                            : canAddAccount
                              ? 'Compte Merchant Center détecté'
                              : 'Connexion Merchant Center requise'}
                        </strong>
                      </p>
                      <p style={{ fontSize: '0.9em', marginBottom: '8px', color: '#888' }}>
                        {merchantExistsInLibrary
                          ? 'Le compte Merchant Center existe dans votre bibliothèque mais les permissions d\'accès sont insuffisantes.'
                          : canAddAccount
                            ? `Le compte Merchant ${detectedMerchantId} a été détecté via vos campagnes Shopping. Ajoutez-le à votre bibliothèque pour voir les groupes de produits.`
                            : `Le compte Merchant ${detectedMerchantId} (lié à vos campagnes Shopping) nécessite une connexion avec un compte Google ayant accès à ce Merchant Center.`}
                      </p>
                      {merchantStatus?.linkedMerchantIds?.length > 0 && (
                        <p style={{ fontSize: '0.85em', marginBottom: '16px', color: '#666', backgroundColor: '#f0f4f8', padding: '8px 12px', borderRadius: '4px', display: 'inline-block' }}>
                          Merchant Center détecté : <strong>{detectedMerchantId}</strong>
                        </p>
                      )}
                      {userRole !== 'CLIENT' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', marginTop: '12px' }}>
                          {canAddAccount ? (
                            /* Bouton pour ajouter le compte à la bibliothèque (sans OAuth) */
                            <button
                              onClick={() => handleAddMerchant(detectedMerchantId)}
                              disabled={addingMerchant}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 20px',
                                backgroundColor: addingMerchant ? '#93c5fd' : '#4285F4',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: addingMerchant ? 'wait' : 'pointer',
                                fontSize: '0.95em',
                                fontWeight: '500'
                              }}
                            >
                              <Plus size={16} />
                              {addingMerchant ? 'Ajout en cours...' : `Ajouter le compte ${detectedMerchantId}`}
                            </button>
                          ) : (
                            /* Bouton pour reconnecter OAuth */
                            <button
                              onClick={() => {
                                // Lancer OAuth pour reconnecter avec un compte qui a accès au Merchant détecté
                                const returnUrl = encodeURIComponent(buildReturnUrl());
                                window.location.href = `${config.API_URL}/api/auth/google-merchant-api?token=${token}&returnUrl=${returnUrl}`;
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 20px',
                                backgroundColor: '#4285F4',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.95em',
                                fontWeight: '500'
                              }}
                            >
                              <Link2 size={16} />
                              {merchantExistsInLibrary ? 'Reconnecter avec les permissions' : `Connecter le compte ${detectedMerchantId}`}
                            </button>
                          )}
                          <p style={{ fontSize: '0.8em', color: '#999', margin: 0 }}>
                            {canAddAccount
                              ? 'Un clic suffit, votre connexion Google Merchant est déjà configurée'
                              : `Connectez-vous avec un compte Google ayant accès au Merchant Center ${detectedMerchantId}`}
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()
              ) : merchantStatus?.status === 'NO_ITEM_GROUP_ID' ? (
                <>
                  <p>Aucun groupe de produits trouvé.</p>
                  <p style={{ fontSize: '0.85em', marginTop: '8px' }}>
                    Ce composant nécessite que vos produits aient un <code>item_group_id</code> renseigné dans Merchant Center.
                  </p>
                </>
              ) : (
                <>
                  <p>Aucun groupe de produits trouvé.</p>
                  <p style={{ fontSize: '0.85em', marginTop: '8px' }}>
                    Ce composant nécessite que vos produits aient un <code>item_group_id</code> renseigné dans Merchant Center.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="paire-top-flop" style={{ display: 'flex', gap: '20px' }}>
              {/* Top Groupes */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 15px 0' }}>
                  <h5 style={{ margin: 0, color: '#2e7d32', fontSize: '1.1em' }}>
                    <Trophy size={16} />&nbsp;Top 5 Groupes (Plus vendeurs)
                  </h5>
                  {userRole !== 'CLIENT' && (
                    <button
                      onClick={() => setShowMetricsConfigTop(true)}
                      className="metrics-settings-btn"
                      title="Configurer les métriques"
                      style={{ padding: '4px', borderRadius: '4px' }}
                    >
                      <Settings size={14} />
                    </button>
                  )}
                </div>
                <div className="campaigns-table-wrapper">
                  <table className="campaigns-table" key={selectedMetrics.join(",")} style={{ tableLayout: "auto" }}>
                    <thead>
                      <tr>
                        <th>Groupe</th>
                        {isTopMetricVisible('impressions') && <th className="numeric">Impr.</th>}
                        {isTopMetricVisible('clics') && <th className="numeric">Clics</th>}
                        {isTopMetricVisible('ctr') && <th className="numeric">CTR</th>}
                        {isTopMetricVisible('cpc') && <th className="numeric">CPC</th>}
                        {isTopMetricVisible('cout') && <th className="numeric">Coût</th>}
                        {isTopMetricVisible('taux_conv') && <th className="numeric">Taux conv.</th>}
                        {isTopMetricVisible('conv') && <th className="numeric">Conv.</th>}
                        {isTopMetricVisible('cpa') && <th className="numeric">CPA</th>}
                        {isTopMetricVisible('valeur_conv') && <th className="numeric">Valeur Conv.</th>}
                        {isTopMetricVisible('cos') && <th className="numeric">COS</th>}
                        {isTopMetricVisible('panier_moyen') && <th className="numeric">Panier moy.</th>}
                        {isTopMetricVisible('roas') && <th className="numeric">ROAS</th>}
                        {isTopMetricVisible('roi') && <th className="numeric">ROI</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {topItemGroups.length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', color: '#666' }}>
                            Aucune donnée disponible pour la période sélectionnée
                          </td>
                        </tr>
                      ) : (
                        topItemGroups.map((group, index) => {
                          // Trouver le groupe correspondant dans les données de la période précédente
                          const prevGroup = compareEnabled && previousTopItemGroups.length > 0
                            ? previousTopItemGroups.find(p => p.itemGroupId === group.itemGroupId)
                            : null;
                          const hasComparison = !!prevGroup;
                          const prevConversionRate = prevGroup && prevGroup.clicks > 0 ? (prevGroup.conversions / prevGroup.clicks * 100) : 0;
                          const prevCpa = prevGroup && prevGroup.conversions > 0 ? (prevGroup.cost / prevGroup.conversions) : 0;
                          const prevCos = prevGroup && prevGroup.conversionsValue > 0 ? (prevGroup.cost / prevGroup.conversionsValue * 100) : 0;

                          const ctr = group.impressions > 0 ? (group.clicks / group.impressions * 100) : 0;
                          const cpc = group.clicks > 0 ? (group.cost / group.clicks) : 0;
                          const roas = group.cost > 0 ? (group.conversionsValue / group.cost) : 0;
                          const roi = (group.cost > 0 && group.conversionsValue > 0) ? ((group.conversionsValue - group.cost) / group.cost * 100) : 0;
                          const prevCtr = prevGroup && prevGroup.impressions > 0 ? (prevGroup.clicks / prevGroup.impressions * 100) : 0;
                          const prevCpc = prevGroup && prevGroup.clicks > 0 ? (prevGroup.cost / prevGroup.clicks) : 0;
                          const prevRoas = prevGroup && prevGroup.cost > 0 ? (prevGroup.conversionsValue / prevGroup.cost) : 0;
                          const prevRoi = prevGroup && prevGroup.cost > 0 && prevGroup.conversionsValue > 0 ? ((prevGroup.conversionsValue - prevGroup.cost) / prevGroup.cost * 100) : 0;

                          return (
                            <tr key={index}>
                              <td style={{ maxWidth: '200px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                <div>
                                  <strong>{group.itemGroupId || 'Groupe non spécifié'}</strong>
                                  {group.productCount > 1 && (
                                    <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '8px' }}>
                                      ({group.productCount} variantes)
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '0.85em', color: '#888', marginTop: '2px' }}>
                                  {group.title}
                                </div>
                              </td>
                              {isTopMetricVisible('impressions') && (
                                <td className="numeric">
                                  {formatNumber(group.impressions || 0)}
                                  {hasComparison && renderVariation(calculateVariation(group.impressions || 0, prevGroup?.impressions || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('clics') && (
                                <td className="numeric">
                                  {formatNumber(group.clicks || 0)}
                                  {hasComparison && renderVariation(calculateVariation(group.clicks || 0, prevGroup.clicks || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('ctr') && (
                                <td className="numeric">
                                  {formatNumber(ctr, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
                                </td>
                              )}
                              {isTopMetricVisible('cpc') && (
                                <td className="numeric">
                                  €{formatNumber(cpc, 2)}
                                  {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
                                </td>
                              )}
                              {isTopMetricVisible('cout') && (
                                <td className="numeric">
                                  €{formatNumber(group.cost || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(group.cost || 0, prevGroup?.cost || 0), true)}
                                </td>
                              )}
                              {isTopMetricVisible('taux_conv') && (
                                <td className="numeric">
                                  {formatNumber(group.conversionRate || 0, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(group.conversionRate || 0, prevConversionRate))}
                                </td>
                              )}
                              {isTopMetricVisible('conv') && (
                                <td className="numeric">
                                  {formatNumber(group.conversions || 0, 1)}
                                  {hasComparison && renderVariation(calculateVariation(group.conversions || 0, prevGroup.conversions || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('cpa') && (
                                <td className={`numeric ${getCpaClassName(group.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                  {group.conversions > 0 ? `€${formatNumber(group.cpa || 0, 2)}` : '∞'}
                                  {hasComparison && renderVariation(calculateVariation(group.cpa || 0, prevCpa), true)}
                                </td>
                              )}
                              {isTopMetricVisible('valeur_conv') && (
                                <td className="numeric">
                                  €{formatNumber(group.conversionsValue || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(group.conversionsValue || 0, prevGroup?.conversionsValue || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('cos') && (
                                <td className={`numeric ${getCosClassName(group.cos, selectedAccount?.id || 'global', moduleId)}`}>
                                  {formatNumber(group.cos || 0, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(group.cos || 0, prevCos), true)}
                                </td>
                              )}
                              {isTopMetricVisible('panier_moyen') && (
                                <td className="numeric">
                                  €{formatNumber(group.conversions > 0 ? (group.conversionsValue || 0) / group.conversions : 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(
                                    group.conversions > 0 ? (group.conversionsValue || 0) / group.conversions : 0,
                                    (prevGroup?.conversionsValue || 0) / prevGroup.conversions
                                  ))}
                                </td>
                              )}
                              {isTopMetricVisible('roas') && (
                                <td className="numeric">
                                  {formatNumber(roas, 2)}
                                  {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                                </td>
                              )}
                              {isTopMetricVisible('roi') && (
                                <td className="numeric">
                                  {formatNumber(roi, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {topItemGroups.length > 0 && topTotals && (() => {
                      const totalCtr = topTotals.impressions > 0 ? (topTotals.clicks / topTotals.impressions * 100) : 0;
                      const totalCpc = topTotals.clicks > 0 ? (topTotals.cost / topTotals.clicks) : 0;
                      const totalRoas = topTotals.cost > 0 ? (topTotals.conversionsValue / topTotals.cost) : 0;
                      const totalRoi = topTotals.cost > 0 ? ((topTotals.conversionsValue - topTotals.cost) / topTotals.cost * 100) : 0;
                      const prevCtr = previousTopTotals && previousTopTotals.impressions > 0 ? (previousTopTotals.clicks / previousTopTotals.impressions * 100) : 0;
                      const prevCpc = previousTopTotals && previousTopTotals.clicks > 0 ? (previousTopTotals.cost / previousTopTotals.clicks) : 0;
                      const prevRoas = previousTopTotals && previousTopTotals.cost > 0 ? (previousTopTotals.conversionsValue / previousTopTotals.cost) : 0;
                      const prevRoi = previousTopTotals && previousTopTotals.cost > 0 ? ((previousTopTotals.conversionsValue - previousTopTotals.cost) / previousTopTotals.cost * 100) : 0;
                      return (
                      <tfoot>
                        <tr className="totals-row">
                          <td><strong>TOTAL</strong></td>
                          {isTopMetricVisible('impressions') && (
                            <td className="numeric">
                              <strong>{formatNumber(topTotals.impressions)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.impressions, previousTopTotals.impressions))}
                            </td>
                          )}
                          {isTopMetricVisible('clics') && (
                            <td className="numeric">
                              <strong>{formatNumber(topTotals.clicks)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.clicks, previousTopTotals.clicks))}
                            </td>
                          )}
                          {isTopMetricVisible('ctr') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalCtr, 2)}%</strong>
                              {previousTopTotals && renderVariation(calculateVariation(totalCtr, prevCtr))}
                            </td>
                          )}
                          {isTopMetricVisible('cpc') && (
                            <td className="numeric">
                              <strong>€{formatNumber(totalCpc, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(totalCpc, prevCpc), true)}
                            </td>
                          )}
                          {isTopMetricVisible('cout') && (
                            <td className="numeric">
                              <strong>€{formatNumber(topTotals.cost, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.cost, previousTopTotals.cost), true)}
                            </td>
                          )}
                          {isTopMetricVisible('taux_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(topTotals.conversionRate, 2)}%</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.conversionRate, previousTopTotals.conversionRate))}
                            </td>
                          )}
                          {isTopMetricVisible('conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(topTotals.conversions, 1)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.conversions, previousTopTotals.conversions))}
                            </td>
                          )}
                          {isTopMetricVisible('cpa') && (
                            <td className={`numeric ${getCpaClassName(topTotals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{topTotals.conversions > 0 ? `€${formatNumber(topTotals.cpa, 2)}` : '∞'}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.cpa, previousTopTotals.cpa), true)}
                            </td>
                          )}
                          {isTopMetricVisible('valeur_conv') && (
                            <td className="numeric">
                              <strong>€{formatNumber(topTotals.conversionsValue, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.conversionsValue, previousTopTotals.conversionsValue))}
                            </td>
                          )}
                          {isTopMetricVisible('cos') && (
                            <td className={`numeric ${getCosClassName(topTotals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{formatNumber(topTotals.cos, 1)}%</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.cos, previousTopTotals.cos), true)}
                            </td>
                          )}
                          {isTopMetricVisible('panier_moyen') && (
                            <td className="numeric">
                              <strong>€{formatNumber(topTotals.conversions > 0 ? topTotals.conversionsValue / topTotals.conversions : 0, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(
                                topTotals.conversions > 0 ? topTotals.conversionsValue / topTotals.conversions : 0,
                                previousTopTotals.conversionsValue / previousTopTotals.conversions
                              ))}
                            </td>
                          )}
                          {isTopMetricVisible('roas') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoas, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(totalRoas, prevRoas))}
                            </td>
                          )}
                          {isTopMetricVisible('roi') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoi, 1)}%</strong>
                              {previousTopTotals && renderVariation(calculateVariation(totalRoi, prevRoi))}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    );})()}
                  </table>
                </div>
              </div>

              {/* Flop Groupes */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 15px 0' }}>
                  <h5 style={{ margin: 0, color: '#d32f2f', fontSize: '1.1em' }}>
                    <TrendingDown size={16} />&nbsp;Flop 5 Groupes (Plus cliqués sans vente)
                  </h5>
                  {userRole !== 'CLIENT' && (
                    <button
                      onClick={() => setShowMetricsConfigFlop(true)}
                      className="metrics-settings-btn"
                      title="Configurer les métriques"
                      style={{ padding: '4px', borderRadius: '4px' }}
                    >
                      <Settings size={14} />
                    </button>
                  )}
                </div>
                <div className="campaigns-table-wrapper">
                  <table className="campaigns-table" key={selectedMetrics.join(",")} style={{ tableLayout: "auto" }}>
                    <thead>
                      <tr>
                        <th>Groupe</th>
                        {isFlopMetricVisible('impressions') && <th className="numeric">Impr.</th>}
                        {isFlopMetricVisible('clics') && <th className="numeric">Clics</th>}
                        {isFlopMetricVisible('ctr') && <th className="numeric">CTR</th>}
                        {isFlopMetricVisible('cpc') && <th className="numeric">CPC</th>}
                        {isFlopMetricVisible('cout') && <th className="numeric">Coût</th>}
                        {isFlopMetricVisible('taux_conv') && <th className="numeric">Taux conv.</th>}
                        {isFlopMetricVisible('conv') && <th className="numeric">Conv.</th>}
                        {isFlopMetricVisible('cpa') && <th className="numeric">CPA</th>}
                        {isFlopMetricVisible('valeur_conv') && <th className="numeric">Valeur Conv.</th>}
                        {isFlopMetricVisible('cos') && <th className="numeric">COS</th>}
                        {isFlopMetricVisible('panier_moyen') && <th className="numeric">Panier moy.</th>}
                        {isFlopMetricVisible('roas') && <th className="numeric">ROAS</th>}
                        {isFlopMetricVisible('roi') && <th className="numeric">ROI</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {flopItemGroups.length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', color: '#666' }}>
                            Aucune donnée disponible pour la période sélectionnée
                          </td>
                        </tr>
                      ) : (
                        flopItemGroups.map((group, index) => {
                          // Trouver le groupe correspondant dans les données de la période précédente
                          const prevGroup = compareEnabled && previousFlopItemGroups.length > 0
                            ? previousFlopItemGroups.find(p => p.itemGroupId === group.itemGroupId)
                            : null;
                          const hasComparison = !!prevGroup;
                          const ctr = group.impressions > 0 ? (group.clicks / group.impressions * 100) : 0;
                          const cpc = group.clicks > 0 ? (group.cost / group.clicks) : 0;
                          const convRate = group.clicks > 0 ? (group.conversions / group.clicks * 100) : 0;
                          const roas = group.cost > 0 ? (group.conversionsValue / group.cost) : 0;
                          const roi = (group.cost > 0 && group.conversionsValue > 0) ? ((group.conversionsValue - group.cost) / group.cost * 100) : 0;
                          const prevCtr = prevGroup && prevGroup.impressions > 0 ? (prevGroup.clicks / prevGroup.impressions * 100) : 0;
                          const prevCpc = prevGroup && prevGroup.clicks > 0 ? (prevGroup.cost / prevGroup.clicks) : 0;
                          const prevConvRate = prevGroup && prevGroup.clicks > 0 ? (prevGroup.conversions / prevGroup.clicks * 100) : 0;
                          const prevCpa = prevGroup && prevGroup.conversions > 0 ? (prevGroup.cost / prevGroup.conversions) : 0;
                          const prevCos = prevGroup && prevGroup.conversionsValue > 0 ? (prevGroup.cost / prevGroup.conversionsValue * 100) : 0;
                          const prevRoas = prevGroup && prevGroup.cost > 0 ? (prevGroup.conversionsValue / prevGroup.cost) : 0;
                          const prevRoi = prevGroup && prevGroup.cost > 0 && prevGroup.conversionsValue > 0 ? ((prevGroup.conversionsValue - prevGroup.cost) / prevGroup.cost * 100) : 0;

                          return (
                            <tr key={index}>
                              <td style={{ maxWidth: '200px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                <div>
                                  <strong>{group.itemGroupId || 'Groupe non spécifié'}</strong>
                                  {group.productCount > 1 && (
                                    <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '8px' }}>
                                      ({group.productCount} variantes)
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '0.85em', color: '#888', marginTop: '2px' }}>
                                  {group.title}
                                </div>
                              </td>
                              {isFlopMetricVisible('impressions') && (
                                <td className="numeric">
                                  {formatNumber(group.impressions || 0)}
                                  {hasComparison && renderVariation(calculateVariation(group.impressions || 0, prevGroup?.impressions || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('clics') && (
                                <td className="numeric">
                                  {formatNumber(group.clicks || 0)}
                                  {hasComparison && renderVariation(calculateVariation(group.clicks || 0, prevGroup.clicks || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('ctr') && (
                                <td className="numeric">
                                  {formatNumber(ctr, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
                                </td>
                              )}
                              {isFlopMetricVisible('cpc') && (
                                <td className="numeric">
                                  €{formatNumber(cpc, 2)}
                                  {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('cout') && (
                                <td className="numeric">
                                  €{formatNumber(group.cost || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(group.cost || 0, prevGroup?.cost || 0), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('taux_conv') && (
                                <td className="numeric">
                                  {formatNumber(convRate, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(convRate, prevConvRate))}
                                </td>
                              )}
                              {isFlopMetricVisible('conv') && (
                                <td className="numeric">
                                  {formatNumber(group.conversions || 0, 1)}
                                  {hasComparison && renderVariation(calculateVariation(group.conversions || 0, prevGroup?.conversions || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('cpa') && (
                                <td className={`numeric ${getCpaClassName(group.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                  {group.conversions > 0 ? `€${formatNumber(group.cpa || 0, 2)}` : '∞'}
                                  {hasComparison && renderVariation(calculateVariation(group.cpa || 0, prevCpa), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('valeur_conv') && (
                                <td className="numeric">
                                  €{formatNumber(group.conversionsValue || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(group.conversionsValue || 0, prevGroup?.conversionsValue || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('cos') && (
                                <td className={`numeric ${getCosClassName(group.cos, selectedAccount?.id || 'global', moduleId)}`}>
                                  {formatNumber(group.cos || 0, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(group.cos || 0, prevCos), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('panier_moyen') && (
                                <td className="numeric">
                                  €{formatNumber(group.conversions > 0 ? (group.conversionsValue || 0) / group.conversions : 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(
                                    group.conversions > 0 ? (group.conversionsValue || 0) / group.conversions : 0,
                                    (prevGroup?.conversionsValue || 0) / prevGroup.conversions
                                  ))}
                                </td>
                              )}
                              {isFlopMetricVisible('roas') && (
                                <td className="numeric">
                                  {formatNumber(roas, 2)}
                                  {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                                </td>
                              )}
                              {isFlopMetricVisible('roi') && (
                                <td className="numeric">
                                  {formatNumber(roi, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {flopItemGroups.length > 0 && flopTotals && (() => {
                      const totalCtr = flopTotals.impressions > 0 ? (flopTotals.clicks / flopTotals.impressions * 100) : 0;
                      const totalConvRate = flopTotals.clicks > 0 ? (flopTotals.conversions / flopTotals.clicks * 100) : 0;
                      const totalRoas = flopTotals.cost > 0 ? (flopTotals.conversionsValue / flopTotals.cost) : 0;
                      const totalRoi = flopTotals.cost > 0 ? ((flopTotals.conversionsValue - flopTotals.cost) / flopTotals.cost * 100) : 0;
                      const prevCtr = previousFlopTotals && previousFlopTotals.impressions > 0 ? (previousFlopTotals.clicks / previousFlopTotals.impressions * 100) : 0;
                      const prevConvRate = previousFlopTotals && previousFlopTotals.clicks > 0 ? (previousFlopTotals.conversions / previousFlopTotals.clicks * 100) : 0;
                      const prevRoas = previousFlopTotals && previousFlopTotals.cost > 0 ? (previousFlopTotals.conversionsValue / previousFlopTotals.cost) : 0;
                      const prevRoi = previousFlopTotals && previousFlopTotals.cost > 0 ? ((previousFlopTotals.conversionsValue - previousFlopTotals.cost) / previousFlopTotals.cost * 100) : 0;
                      return (
                      <tfoot>
                        <tr className="totals-row">
                          <td><strong>TOTAL</strong></td>
                          {isFlopMetricVisible('impressions') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopTotals.impressions)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.impressions, previousFlopTotals.impressions))}
                            </td>
                          )}
                          {isFlopMetricVisible('clics') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopTotals.clicks)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.clicks, previousFlopTotals.clicks))}
                            </td>
                          )}
                          {isFlopMetricVisible('ctr') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalCtr, 2)}%</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(totalCtr, prevCtr))}
                            </td>
                          )}
                          {isFlopMetricVisible('cpc') && (
                            <td className="numeric">
                              <strong>€{formatNumber(flopTotals.cpc, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.cpc, previousFlopTotals.cpc), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('cout') && (
                            <td className="numeric">
                              <strong>€{formatNumber(flopTotals.cost, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.cost, previousFlopTotals.cost), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('taux_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalConvRate, 2)}%</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(totalConvRate, prevConvRate))}
                            </td>
                          )}
                          {isFlopMetricVisible('conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopTotals.conversions, 1)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.conversions, previousFlopTotals.conversions))}
                            </td>
                          )}
                          {isFlopMetricVisible('cpa') && (
                            <td className={`numeric ${getCpaClassName(flopTotals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{flopTotals.conversions > 0 ? `€${formatNumber(flopTotals.cpa, 2)}` : '∞'}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.cpa, previousFlopTotals.cpa), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('valeur_conv') && (
                            <td className="numeric">
                              <strong>€{formatNumber(flopTotals.conversionsValue, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.conversionsValue, previousFlopTotals.conversionsValue))}
                            </td>
                          )}
                          {isFlopMetricVisible('cos') && (
                            <td className={`numeric ${getCosClassName(flopTotals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{formatNumber(flopTotals.cos, 1)}%</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.cos, previousFlopTotals.cos), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('panier_moyen') && (
                            <td className="numeric">
                              <strong>€{formatNumber(flopTotals.conversions > 0 ? flopTotals.conversionsValue / flopTotals.conversions : 0, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(
                                flopTotals.conversions > 0 ? flopTotals.conversionsValue / flopTotals.conversions : 0,
                                previousFlopTotals.conversionsValue / previousFlopTotals.conversions
                              ))}
                            </td>
                          )}
                          {isFlopMetricVisible('roas') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoas, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(totalRoas, prevRoas))}
                            </td>
                          )}
                          {isFlopMetricVisible('roi') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoi, 1)}%</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(totalRoi, prevRoi))}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    );})()}
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de configuration des métriques pour Top */}
      <MetricsConfigModal
        isOpen={showMetricsConfigTop}
        onClose={() => setShowMetricsConfigTop(false)}
        componentId="topflop_itemgroups_top"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Top 5 Groupes (Plus vendeurs)"
        hideApplyToAll={true}
      />

      {/* Modal de configuration des métriques pour Flop */}
      <MetricsConfigModal
        isOpen={showMetricsConfigFlop}
        onClose={() => setShowMetricsConfigFlop(false)}
        componentId="topflop_itemgroups_flop"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Flop 5 Groupes (Plus cliqués sans vente)"
        hideApplyToAll={true}
      />
    </div>
  );
});

TopFlopItemGroupsTableModular.displayName = 'TopFlopItemGroupsTableModular';

export default TopFlopItemGroupsTableModular;
